/**
 * Compact PWA statistics widget for the admin area.
 * Shows the most useful metrics in 4 cards + recent activity feed.
 */
import React, { useEffect, useState } from 'react';
import { Smartphone, Download, Eye, MousePointerClick } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StatCard = ({ icon: Icon, color, value, label, testId }) => (
  <div
    className="bg-white/5 border border-white/10 rounded-lg p-4"
    data-testid={testId}
  >
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${color}22` }}>
        <Icon size={20} style={{ color }} />
      </div>
      <div>
        <div className="text-2xl font-bold text-white">{value}</div>
        <div className="text-xs text-white/60">{label}</div>
      </div>
    </div>
  </div>
);

const PwaStatsWidget = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/admin/pwa-stats`);
        if (mounted) setStats(data);
      } catch (e) {
        if (mounted) setErr(e?.message || 'Errore');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  if (loading) {
    return (
      <div className="bg-white/5 border border-white/10 rounded-lg p-4 text-white/60 text-sm">
        Caricamento statistiche PWA...
      </div>
    );
  }
  if (err || !stats) {
    return (
      <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-red-300 text-sm">
        Impossibile caricare le statistiche PWA: {err || 'errore sconosciuto'}
      </div>
    );
  }

  const { totals, unique_devices_installed, unique_devices_launched, by_platform, recent } = stats;
  const acceptRate = totals.install_prompt_shown
    ? Math.round((totals.install_accepted / totals.install_prompt_shown) * 100)
    : 0;

  return (
    <div className="space-y-4" data-testid="pwa-stats-widget">
      <div className="flex items-center gap-2">
        <Smartphone size={20} style={{ color: '#FF6600' }} />
        <h2 className="text-lg font-semibold text-white">Statistiche App (PWA)</h2>
      </div>

      <div className="bg-blue-500/5 border border-blue-500/20 rounded-lg p-3 text-xs text-blue-200/80">
        <strong>Nota:</strong> il numero degli installati include anche dispositivi iOS rilevati al primo lancio in modalità app
        (Apple non notifica l&apos;installazione al sito). Ogni dispositivo viene contato una volta al giorno.
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard
          icon={Download}
          color="#22c55e"
          value={unique_devices_installed}
          label="Dispositivi che usano l'app"
          testId="pwa-stat-installed"
        />
        <StatCard
          icon={Smartphone}
          color="#FF6600"
          value={unique_devices_launched}
          label="Dispositivi che hanno aperto l'app"
          testId="pwa-stat-launched"
        />
        <StatCard
          icon={Eye}
          color="#3b82f6"
          value={totals.install_prompt_shown}
          label="Inviti mostrati"
          testId="pwa-stat-shown"
        />
        <StatCard
          icon={MousePointerClick}
          color="#a855f7"
          value={`${acceptRate}%`}
          label="Tasso di accettazione"
          testId="pwa-stat-rate"
        />
      </div>

      {/* Platform split */}
      {Object.keys(by_platform || {}).length > 0 && (
        <div className="bg-white/5 border border-white/10 rounded-lg p-4">
          <div className="text-xs text-white/60 mb-2">Installazioni per piattaforma</div>
          <div className="flex flex-wrap gap-3 text-sm">
            {Object.entries(by_platform).map(([plat, count]) => (
              <span key={plat} className="px-3 py-1 rounded-full bg-white/10 text-white">
                <span className="text-white/60 capitalize">{plat}:</span>{' '}
                <span className="font-semibold">{count}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Recent activity */}
      {Array.isArray(recent) && recent.length > 0 && (
        <details className="bg-white/5 border border-white/10 rounded-lg" data-testid="pwa-recent-details">
          <summary className="px-4 py-3 cursor-pointer text-sm text-white/80 hover:bg-white/5">
            Attività recente (ultimi {recent.length} eventi)
          </summary>
          <ul className="px-4 pb-3 text-xs text-white/70 space-y-1 max-h-64 overflow-y-auto">
            {recent.map((r, idx) => (
              <li key={`pwa-event-${idx}`} className="flex justify-between border-b border-white/5 py-1">
                <span>
                  <span className="text-white/90">{r.event}</span>
                  <span className="text-white/40"> · {r.platform}</span>
                </span>
                <span className="text-white/40">
                  {r.created_at ? new Date(r.created_at).toLocaleString('it-IT') : ''}
                </span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
};

export default PwaStatsWidget;
